import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Save,
  MessageSquare,
  User,
  Quote,
  Star,
  Building,
  Image as ImageIcon,
  Link as LinkIcon,
  LayoutTemplate
} from 'lucide-react';
import { testimonials, projects } from '@/data/projects';
import { ImageUpload } from '@/components/ui/image-upload';
import { useUpload } from '@/hooks/use-upload';

const testimonialSchema = z.object({
  name: z.string().min(1, 'Nome do cliente é obrigatório'),
  role: z.string().min(1, 'Cargo é obrigatório'),
  company: z.string().min(1, 'Empresa é obrigatória'),
  quote: z.string().min(10, 'Depoimento deve ter pelo menos 10 caracteres'),
  avatar: z.string().min(1, 'Avatar é obrigatório'),
  projectId: z.string().optional(),
});

type TestimonialFormData = z.infer<typeof testimonialSchema>;

const DepoimentoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [hasProject, setHasProject] = useState(false);
  const { uploadImage, base64ToFile } = useUpload();

  // Mock data simulation - real implementation would fetch from API
  const testimonial = isEditing ? testimonials.find(t => t.id === id) : null;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TestimonialFormData>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: testimonial || {
      name: '',
      role: '',
      company: '',
      quote: '',
      avatar: '',
      projectId: '',
    },
  });

  const watchedValues = watch();

  useEffect(() => {
    if (watchedValues.projectId) {
      setHasProject(true);
    }
  }, [watchedValues.projectId]);

  const onSubmit = async (data: TestimonialFormData) => {
    setIsLoading(true);
    try {
      let avatarUrl = data.avatar;

      // Upload Avatar if Base64
      if (avatarUrl.startsWith('data:')) {
        const file = await base64ToFile(avatarUrl, `testimonial-avatar-${Date.now()}.png`);
        const uploaded = await uploadImage(file, 'images');
        if (uploaded) avatarUrl = uploaded;
      }

      const finalData = {
        ...data,
        avatar: avatarUrl,
        projectId: hasProject ? data.projectId : undefined
      };

      console.log('Salvando depoimento:', finalData);
      await new Promise(resolve => setTimeout(resolve, 1000));
      // In real app: createMutation.mutateAsync(finalData)
      navigate('/admin/depoimentos');
    } catch (error) {
      console.error('Erro ao salvar depoimento:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectChange = (projectId: string) => {
    setValue('projectId', projectId);
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/depoimentos')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditing ? 'Editar Depoimento' : 'Novo Depoimento'}
            </h1>
            <p className="text-muted-foreground hidden md:block">
              {isEditing ? 'Gerencie o feedback do cliente' : 'Adicione uma nova prova social'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleSubmit(onSubmit)} disabled={isLoading} className="shadow-lg bg-blue-600 hover:bg-blue-700 transition-all">
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? 'Salvando...' : 'Salvar Depoimento'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

          {/* Main Content */}
          <div className="xl:col-span-2 space-y-8">

            {/* Client Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="w-5 h-5 text-blue-500" />
                  <span>Informações do Cliente</span>
                </CardTitle>
                <CardDescription>Dados de identificação</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome do Cliente *</Label>
                    <Input id="name" {...register('name')} placeholder="Ex: Ana Silva" className="font-medium" />
                    {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Cargo *</Label>
                    <Input id="role" {...register('role')} placeholder="Ex: CEO" />
                    {errors.role && <p className="text-sm text-destructive">{errors.role.message}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Empresa *</Label>
                  <Input id="company" {...register('company')} placeholder="Ex: Tech Solutions" />
                  {errors.company && <p className="text-sm text-destructive">{errors.company.message}</p>}
                </div>
              </CardContent>
            </Card>

            {/* Testimonial Content */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MessageSquare className="w-5 h-5 text-blue-500" />
                  <span>Conteúdo</span>
                </CardTitle>
                <CardDescription>O depoimento do cliente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="quote">Depoimento *</Label>
                  <Textarea
                    id="quote"
                    {...register('quote')}
                    placeholder="O que o cliente disse..."
                    rows={6}
                    className="resize-y text-base"
                  />
                  {errors.quote && <p className="text-sm text-destructive">{errors.quote.message}</p>}
                </div>
              </CardContent>
            </Card>

            {/* Avatar Image */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ImageIcon className="w-5 h-5 text-blue-500" />
                  <span>Avatar do Cliente</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Foto *</Label>
                  <ImageUpload
                    value={watchedValues.avatar}
                    onChange={(url) => setValue('avatar', url, { shouldValidate: true })}
                    label="Upload do Avatar"
                  />
                  {errors.avatar && <p className="text-sm text-destructive">{errors.avatar.message}</p>}
                </div>
              </CardContent>
            </Card>

            {/* Association */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <LinkIcon className="w-5 h-5 text-blue-500" />
                  <span>Associação</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
                  <Checkbox
                    id="hasProject"
                    checked={hasProject}
                    onCheckedChange={(checked) => setHasProject(checked as boolean)}
                  />
                  <Label htmlFor="hasProject" className="cursor-pointer">
                    Associar a um projeto específico
                  </Label>
                </div>

                {hasProject && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                    <Label>Selecione o Projeto</Label>
                    <Select value={watchedValues.projectId || ''} onValueChange={handleProjectChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Escolha um projeto" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>

          {/* Sidebar - Live Preview */}
          <div className="space-y-6">
            <Card className="bg-slate-950 border-slate-800 sticky top-24 overflow-hidden">
              <CardHeader className="border-b border-slate-800 pb-4">
                <CardTitle className="flex items-center space-x-2 text-white">
                  <LayoutTemplate className="w-5 h-5 text-blue-400" />
                  <span>Live Preview</span>
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Visualização do Card
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 bg-slate-900 flex justify-center">
                {/* Simulated Testimonial Card */}
                <div className="w-full max-w-[320px] bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl relative">
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-500/10 rounded-full">
                      <Quote className="h-4 w-4 text-blue-400" />
                    </div>
                    <div className="flex items-center ml-auto space-x-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-3 w-3 fill-current text-yellow-500" />
                      ))}
                    </div>
                  </div>

                  <blockquote className="text-sm leading-relaxed text-slate-300 mb-6 italic min-h-[80px]">
                    "{watchedValues.quote || 'O depoimento do cliente aparecerá aqui...'}"
                  </blockquote>

                  <div className="flex items-center pt-4 border-t border-slate-800/50">
                    <div className="w-10 h-10 rounded-full bg-slate-800 overflow-hidden mr-3 shrink-0">
                      {watchedValues.avatar ? (
                        <img src={watchedValues.avatar} className="w-full h-full object-cover" alt="Avatar" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500">
                          <User className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-200 text-sm truncate">
                        {watchedValues.name || 'Nome do Cliente'}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {watchedValues.role || 'Cargo'} • {watchedValues.company || 'Empresa'}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info Sidebar */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Dicas</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>• Use fotos de alta qualidade para o avatar.</p>
                <p>• Depoimentos com cargos e empresas reais geram mais credibilidade.</p>
                <p>• Mantenha o texto objetivo e focado em resultados.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DepoimentoForm;
